import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleSpin, handleGetSpinParams, handleBuyFeature } from "../../service";
import { initializeGame, refreshGameData } from "./gameSettingsSlice";
import { Settings } from "../../settings";

// Thunk for buying bonus feature
export const rgsBuyFeature = createAsyncThunk(
  "spin/rgsBuyFeature",
  async ({ level } = {}, { getState, rejectWithValue }) => {
    const state = getState();
    const { bet } = state.game;

    try {
      const result = await handleBuyFeature(bet, level);
      return result;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to buy feature");
    }
  },
);

// Thunk for spinning the reels
export const rgsPlay = createAsyncThunk(
  "spin/rgsPlay",
  async (options = {}, { getState, rejectWithValue }) => {
    const state = getState();
    const { bet, token, featureState } = state.game;
    const freeRoundsActive = state.freeRounds?.status === "active";
    const freeRoundsBet = state.freeRounds?.betValue;
    const availableActions = state.game?.availableActions || ["init", "spin"];
    const { purchasedFeature, purchasedLevel, bonusCost } = options;

    try {
      // Get command and spinOptions from RGS plugin based on universal state
      const { command, spinOptions } = handleGetSpinParams({
        bet,
        featureState,
        freeRoundsActive,
        freeRoundsBet,
        availableActions,
        purchasedFeature,
        purchasedLevel,
      });

      // Response is already transformed by RGS class
      const result = await handleSpin(
        bet,
        token,
        command,
        purchasedFeature,
        spinOptions,
      );

      // Return result with command and bonusCost included (already normalized by RGS)
      return { ...result, command, bonusCost };
    } catch (error) {
      // If error is an object with errors array (API error), return full error object
      // Otherwise return error message
      if (
        error &&
        typeof error === "object" &&
        error.errors &&
        Array.isArray(error.errors)
      ) {
        return rejectWithValue(error);
      } else {
        return rejectWithValue(error.message || "Failed to spin the reels");
      }
    }
  },
  {
    // ── One request in flight at a time ──
    //
    // Last line of defence against a duplicate command. Players were banned
    // for sending a second `respin` 173-542ms after a legitimate one (TAM
    // report 2026-08-13); the server had already closed the round and answered
    // unavailable_action. The specific UI paths that could do that are fixed
    // upstream, but any future one would reintroduce the ban risk, so the
    // duplicate is stopped at the single point every spin passes through.
    //
    // rgsPlayInFlight is the authoritative check — set ONLY by rgsPlay.pending,
    // cleared ONLY by rgsPlay.fulfilled/rejected (both below). Nothing else in
    // the codebase touches it. status/featureSpinStatus are kept as a second
    // layer, but they are NOT reliable alone: setFeatureSpinComplete(true) is
    // shared cleanup used by 15+ unrelated call sites (error recovery, outro
    // transition, warning popups) and its reducer unconditionally forces
    // featureSpinStatus to "succeeded" even while a real request is still
    // pending — verified live (2026-08-14): forcing it mid-flight let a
    // genuine second `respin` reach the dev API 3ms after the first, with
    // the original guard reading "succeeded" and waving it through.
    // ── A failed respin is never retried ──
    //
    // Second half of the ban problem, found by QA 2026-08-14 with two tabs
    // open on the same session: tab B finishes the bonus, tab A does not know
    // and sends a respin to the now-closed round. The server answers
    // unavailable_action. That single error is survivable — the ban comes from
    // sending it TWICE.
    //
    // Nothing stopped the second one: rgsPlay.rejected does NOT refresh
    // availableActions (it stays ["init","respin"]), featureState stays
    // "bonus", potCollection.respinsLeft stays > 0, and the engine's
    // _freezeAllSpinningCells clears _isSpinning and sets featureSpinComplete,
    // so the bonus autoplay loop believes it may spin again. rgsPlayInFlight
    // does not help either — the first request has already settled.
    //
    // After a failed respin our local state provably disagrees with the
    // server, so ANY command we build from it may be illegal. We refuse to
    // send anything until a real re-sync (initializeGame / refreshGameData)
    // has happened; the existing error-recovery flow performs exactly that
    // (freeze → warning popup → ErrorRecoveryOverlay → refreshGameData), and
    // a page reload re-inits, so this cannot wedge the game shut.
    //
    // Deliberately scoped to respins only (isFeatureSpin at rejection time):
    // base-game errors like not_enough_money or bet-limit must stay
    // retryable, and the bans only ever involved respin.
    //
    // NOT COVERED, on purpose: two tabs open on the same session. Each tab is
    // its own client with its own copy of this guard, so each can still send
    // one command from stale state — two tabs, two rejections. Verified
    // 2026-08-14 that PopiPlay's other games behave identically (first respin
    // errors → resync → repeat it in the other tab → ban), so this matches the
    // platform. A cross-tab lock was built and deliberately removed: it made
    // us the only game on the platform behaving differently. The real fix for
    // the multi-tab race belongs on the server (it should not ban a player for
    // two honest clients racing).
    condition: (options, { getState }) => {
      const spin = getState().spin;
      if (
        spin.rgsPlayInFlight ||
        spin.respinBlockedUntilResync ||
        spin.status === "pending" ||
        spin.featureSpinStatus === "pending"
      ) {
        return false;
      }
      return true;
    },
  },
);

// Initial state for the spin slice
const initialState = {
  isSpinning: false, // Indicates if a spin is currently in progress
  status: "idle", // Tracks the status of the API request
  error: null, // Stores any error messages
  manualStop: false, // Flag to track if the spin was manually stopped
  spinComplete: true, // Indicates if the spin has completed
  windupComplete: false, // Indicates if all reels have completed their windup phase
  windupCompletedCount: 0, // Tracks how many reels have completed windup (must reach REEL_COUNT)
  reelsFrozen: false, // Flag to track if reels were frozen due to error (need recovery)
  // Set ONLY by rgsPlay.pending, cleared ONLY by rgsPlay.fulfilled/rejected.
  // Nothing else may write this — it exists so the duplicate-request guard
  // above cannot be defeated by unrelated cleanup dispatches (see condition
  // comment). Do not repurpose for UI state.
  rgsPlayInFlight: false,
  // True after a RESPIN request failed: our state disagrees with the server,
  // so no further command may be sent until a successful re-sync clears it
  // (see the condition guard above). Set only by rgsPlay.rejected, cleared
  // only by initializeGame.fulfilled / refreshGameData.fulfilled.
  respinBlockedUntilResync: false,
  // Feature game states (generic for any feature with parallel spin cycle)
  featureSpinActive: false, // Indicates if feature spin is in progress
  featureSpinStatus: "idle", // Tracks the status of feature spin API request
  featureSpinComplete: true, // Indicates if feature spin has completed
  featureSpinManualStop: false, // Flag to track if feature spin was manually stopped
};

// Create a slice for spin-related state and actions
const spinSlice = createSlice({
  name: "spin",
  initialState,
  reducers: {
    // Action to manually stop a spin
    manualSpinStop: (state) => {
      if (state.isSpinning && !state.manualStop) {
        state.manualStop = true; // Always mark as manual stop when requested
      }
    },

    // Action to manually set spinning state
    manualSpinStart: (state) => {
      state.isSpinning = true; // Set isSpinning
      state.spinComplete = false; // Set spinComplete
    },

    // Action to set the spin completion status
    setSpinComplete: (state, action) => {
      // Allow setting spinComplete for both succeeded and failed status
      // Failed status occurs with error 205 (free rounds) where reels still complete their animation
      if (state.status === "succeeded" || state.status === "failed") {
        state.isSpinning = false; // Mark spinning as false
        state.spinComplete = action.payload; // Update spin completion status
      }
    },

    // Action to mark reels as frozen (need recovery)
    setReelsFrozen: (state, action) => {
      state.reelsFrozen = action.payload;
    },

    // Action to set the windup completion status
    setWindupComplete: (state, action) => {
      state.windupComplete = action.payload; // Update windup completion status
    },

    // CRITICAL: Increment windup counter - called by each reel when it completes windup
    // Only sets windupComplete to true when ALL reels have completed
    incrementWindupCount: (state) => {
      state.windupCompletedCount += 1;

      // Only set windupComplete when ALL reels have finished windup
      if (state.windupCompletedCount === Settings.REEL_COUNT) {
        state.windupComplete = true;
      }
    },

    // Reset windup counter - called when a new spin starts
    resetWindupCount: (state) => {
      state.windupCompletedCount = 0;
      state.windupComplete = false;
    },

    // Feature game actions (generic for any feature with parallel spin cycle)
    // Action to set feature spin active state
    setFeatureSpinActive: (state, action) => {
      state.featureSpinActive = action.payload;
    },

    // Action to manually stop a feature spin
    featureSpinManualStop: (state) => {
      if (state.featureSpinActive && !state.featureSpinManualStop) {
        state.featureSpinManualStop = true;
      }
    },

    // Action to set feature spin completion status
    // Handles both boolean and object payloads: dispatch(setFeatureSpinComplete(true))
    // or dispatch(setFeatureSpinComplete({ complete: true, bet }))
    setFeatureSpinComplete: (state, action) => {
      const complete = typeof action.payload === 'boolean'
        ? action.payload
        : action.payload?.complete ?? action.payload;

      // When completing (true), always allow — exit cleanup needs this to work
      // regardless of featureSpinStatus. When starting (false), guard on status.
      if (complete) {
        state.featureSpinActive = false;
        state.featureSpinComplete = true;
        state.featureSpinStatus = "succeeded"; // Sync status
      } else if (state.featureSpinStatus === "succeeded") {
        state.featureSpinActive = false;
        state.featureSpinComplete = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(rgsPlay.pending, (state) => {
        // Authoritative in-flight flag for the condition guard above — only
        // rgsPlay.pending/fulfilled/rejected touch this field.
        state.rgsPlayInFlight = true;

        // Check if feature spin was already active - if so, this is a feature spin
        // Otherwise, assume it's a regular spin
        const isFeatureSpin = state.featureSpinActive;

        if (isFeatureSpin) {
          // Update feature spin states
          state.featureSpinStatus = "pending";
          state.featureSpinComplete = false;
          state.featureSpinManualStop = false;
        } else {
          // Update regular game states
          state.error = null;
          state.status = "pending";
          state.isSpinning = true;
          state.spinComplete = false;
          state.windupComplete = false;
          state.windupCompletedCount = 0; // Reset counter for new spin
          state.manualStop = false;
        }
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        state.rgsPlayInFlight = false;

        // Use command only: respin = feature spin (base reels not used), spin = base game logic (includes free rounds)
        const isRespin = action.payload.command === "respin";
        const wasFeatureSpin = state.featureSpinActive;

        if (isRespin) {
          state.featureSpinStatus = "succeeded";
          state.featureSpinActive = true;
          state.featureSpinComplete = false;
          state.featureSpinManualStop = false;

          if (wasFeatureSpin) {
            state.isSpinning = false;
            state.status = "idle";
            state.spinComplete = true;
          } else {
            state.status = "succeeded";
            // Do NOT set isSpinning or spinComplete - reel flow handles them
          }
        } else {
          // Spin command: base game or free rounds - same isSpinning logic
          state.status = "succeeded";
          state.featureSpinActive = false;
          state.featureSpinStatus = "idle";
          state.featureSpinComplete = true;
          // Do NOT set isSpinning or spinComplete - reel flow handles them
        }
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        state.rgsPlayInFlight = false;

        // Determine which spin type failed based on current state
        const isFeatureSpin = state.featureSpinActive;

        if (isFeatureSpin) {
          // A respin failed → local state disagrees with the server. Refuse
          // to send anything else until a re-sync (see condition guard).
          state.respinBlockedUntilResync = true;
          // Update feature spin states
          state.featureSpinStatus = "failed";
          state.featureSpinActive = false;
        } else {
          // Update regular game states
          state.status = "failed";
          state.isSpinning = false;
          state.error = action.payload || "Spin failed";
          // spinComplete will be set by ReelController when reels finish stopping
        }
      })
      // Handle feature completion reload from initializeGame
      .addCase(initializeGame.fulfilled, (state, action) => {
        // Fresh server truth — safe to send commands again.
        state.respinBlockedUntilResync = false;

        const { result } = action.payload;
        const { reloadState } = result;

        // Check if we have any feature completion reload from RGS
        if (reloadState?.needsReload && reloadState.phase === "completion") {
          // Set feature spin state to completed (allows dialog trigger effect to fire)
          state.featureSpinStatus = "succeeded";
          state.featureSpinActive = false;
          state.featureSpinComplete = true;
          state.featureSpinManualStop = false;
        }
      })
      // Re-sync after an error (ErrorRecoveryOverlay / WarningPopup dismiss)
      // replaced featureState + availableActions with server truth, so the
      // respin block is released.
      .addCase(refreshGameData.fulfilled, (state) => {
        state.respinBlockedUntilResync = false;
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectIsSpinning = (state) => state.spin.isSpinning;
export const selectSpinStatus = (state) => state.spin.status;
export const selectSpinError = (state) => state.spin.error;
export const selectManualStop = (state) => state.spin.manualStop;
export const selectSpinComplete = (state) => state.spin.spinComplete;
export const selectWindupComplete = (state) => state.spin.windupComplete;
export const selectReelsFrozen = (state) => state.spin.reelsFrozen;
export const selectFeatureSpinActive = (state) =>
  state.spin.featureSpinActive;
export const selectFeatureSpinStatus = (state) => state.spin.featureSpinStatus;
export const selectFeatureSpinComplete = (state) =>
  state.spin.featureSpinComplete;
export const selectFeatureSpinManualStop = (state) =>
  state.spin.featureSpinManualStop;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  manualSpinStop,
  manualSpinStart,
  setSpinComplete,
  setWindupComplete,
  incrementWindupCount,
  resetWindupCount,
  setReelsFrozen,
  setFeatureSpinActive,
  featureSpinManualStop,
  setFeatureSpinComplete,
} = spinSlice.actions;

// Export the reducer to be included in the store
export default spinSlice.reducer;
